// src/components/cases/EmptyCaseState.js
import React, { useRef, useEffect, useState } from 'react';
import AnimatedCharacter from '../ui/AnimatedCharacter';
import { FolderPlus } from 'lucide-react';

/**
 * EmptyCaseState - Empty state shown when user has no cases yet
 * 
 * Features an animated character that:
 * 1. Smoothly scans across the instruction text (left to right)
 * 2. Looks at the "New Case" button in the sidebar
 * 3. Looks directly at the user
 * 4. Repeats the sequence
 * 
 * @param {Object} newCaseButtonRef - Ref to the new case button in the sidebar
 * @param {Function} onButtonHighlight - Callback to show/hide button highlight, receives boolean
 */
const EmptyCaseState = ({ newCaseButtonRef, onButtonHighlight }) => {
  const textRef = useRef(null);
  const characterRef = useRef(null);
  const [lookSequence, setLookSequence] = useState([]);
  const isMountedRef = useRef(true);

  useEffect(() => {
    let timeoutId = null;
    // Calculate positions for the animation sequence
    const calculateSequence = () => {
      const textRect = textRef.current?.getBoundingClientRect();
      const buttonRect = newCaseButtonRef?.current?.getBoundingClientRect();
      const characterRect = characterRef.current?.getBoundingClientRect();

      if (!textRect || !buttonRect || !characterRect) {
        // Retry in a moment if elements aren't ready
        timeoutId = setTimeout(calculateSequence, 100);
        return;
      }

      const sequence = [];
      const textY = textRect.top + textRect.height / 2;

      // 1. Smooth scan across text (left to right)
      // Create multiple intermediate points for smooth movement
      const scanPoints = 20; // More points = smoother scan
      for (let i = 0; i <= scanPoints; i++) {
        const progress = i / scanPoints;
        const x = textRect.left + (textRect.width * progress);
        sequence.push({ 
          x, 
          y: textY, 
          type: 'smooth-scan',
          duration: i === 0 ? .25 : 1.5 / scanPoints, // Slower first movement (1s instead of 0.125s)
          pause: i === 0 ? 300 : (i === scanPoints ? 800 : 0) // Pause at start and end
        });
      }

      // 2. Look at the sidebar new case button
      const buttonStep = { 
        x: buttonRect.left + buttonRect.width / 2, 
        y: buttonRect.top + buttonRect.height / 2,
        duration: 0.6,
        pause: 2000,
        isButtonStep: true
      };
      sequence.push(buttonStep);

      // 3. Look at the user (straight ahead = center of character)
      // TRY DIFFERENT ANIMATIONS: 'bouncy-spin', 'blink', 'partial-spin', or remove specialAnimation
      sequence.push({ 
        x: characterRect.left + characterRect.width / 2, 
        y: characterRect.top + characterRect.height / 2,
        duration: 0.6,
        pause: 500, // Pause before animation
        specialAnimation: 'partial-spin', // Accumulating rotation with bouncy easing
        postAnimationPause: 800, // Pause after animation before restarting loop
        keepButtonHighlight: true // Keep halo visible during this step
      });

      if (isMountedRef.current) {
        setLookSequence(sequence);
      }
    };

    // Calculate on mount and window resize
    calculateSequence();
    window.addEventListener('resize', calculateSequence);

    return () => {
      isMountedRef.current = false;  // Mark as unmounted
      if (timeoutId) {
        clearTimeout(timeoutId);  // Cancel any pending timeout
      }
      window.removeEventListener('resize', calculateSequence);
    };
  }, [newCaseButtonRef]);

  // Handle animation step changes
  const handleStepChange = (target, stepIndex) => {
    // Show highlight when looking at button OR when keeping highlight during user-look step
    const shouldHighlight = target.isButtonStep === true || target.keepButtonHighlight === true;
    if (onButtonHighlight) {
      onButtonHighlight(shouldHighlight);
    } else {
      console.warn('onButtonHighlight callback not provided');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-8 py-12">
      {/* Animated Character - Only render when sequence is ready */}
      <div 
        ref={characterRef}
        className="w-48 h-48 mb-8"
      >
        {lookSequence.length > 0 && (
          <AnimatedCharacter 
            lookAtSequence={lookSequence}
            onStepChange={handleStepChange}
            className="w-full h-full"
          />
        )}
      </div>

      {/* Instruction Text */}
      <p 
        ref={textRef}
        className="text-app-2xl text-app-gray-800 text-center max-w-xl mb-app-md"
      >
        <span>Click the </span>
        <FolderPlus size={26} className="inline-block flex-shrink-0 text-app-gray-500" strokeWidth={2} />
        <span> button to create a new case.</span>
      </p>
      <p
        className="text-app-md text-app-gray-500 text-center max-w-md"
      >
        Once you create one, you can upload documents to it.
      </p>
    </div>
  );
};

export default EmptyCaseState;